<template>
    <pc-capsule
        v-bind="capsuleBinderOverride"
        v-bind:has-body="isAdminPage && canSendCommunications"
        data-test-role="communications-capsule"
        has-table
        v-on="capsuleListeners">
        <template v-slot:capsule-table>
            <pc-loader v-bind:loading="processing || loadingTableData">
                <pc-table
                    id="received-communications-table"
                    v-bind:items="communications"
                    v-bind:columns="columns"
                    v-bind:initial-sort="[[0, 'desc']]"
                    v-bind:empty-text="emptyTable"
                    v-bind:hide-actions="!isAdminPage"
                    key-prop="PublicId"
                    hide-search>
                    <template v-slot:SentTimestamp="communication">
                        {{ communication.SentTimestamp.Display }}
                    </template>
                    <!-- Landing page only -->
                    <template v-slot:MessageSubject="communication">
                        <div class="d-flex align-items-center justify-content-between">
                            <div data-test-role="subject">
                                {{ communication.MessageSubject }}
                            </div>
                            <pc-button
                                class="btn-sm communication-action-button"
                                v-on:click="viewCommunication(communication)">
                                {{ viewButtonLabel }}
                            </pc-button>
                        </div>
                    </template>
                    <!-- Admin page only -->
                    <template v-slot:Description="communication">
                        <div v-if="communication.Description">
                            {{ communication.Description }}
                        </div>
                        <div
                            v-else
                            class="fst-italic text-body-secondary">
                            {{ noDescriptionText }}
                        </div>
                    </template>
                    <!-- Admin page only -->
                    <template v-slot:MessageStatusDisplay="communication">
                        <div v-if="communication.HasDeliveryFailures">
                            <span class="text-bubble-danger">
                                {{ communication.MessageStatusDisplay }}
                            </span>
                        </div>
                        <div v-else>
                            {{ communication.MessageStatusDisplay }}
                        </div>
                    </template>
                    <!-- Landing page only -->
                    <template v-slot:RecipientStatusDisplay="communication">
                        <div v-if="communication.HasDeliveryFailures">
                            <span class="text-bubble-danger">
                                {{ communication.RecipientStatusDisplay }}
                            </span>
                        </div>
                        <div v-else>
                            {{ communication.RecipientStatusDisplay }}
                        </div>
                    </template>
                    <template v-slot:actionsRow="communication">
                        <div
                            v-if="communication.IsDraft && canSendCommunications"
                            class="d-flex align-items-center justify-content-between">
                            <pc-button
                                id="EditCommunicationBtn"
                                v-on:click="editCommunication(communication)">
                                {{ editText }}
                            </pc-button>
                            <pc-button
                                id="DeleteDraftBtn"
                                link
                                v-on:click="deleteDraftCommunication(communication)">
                                {{ deleteDraftButtonLabel }}
                            </pc-button>
                        </div>
                        <div v-else>
                            <pc-button
                                id="ViewCommunicationBtn"
                                v-on:click="viewCommunication(communication)">
                                {{ viewButtonLabel }}
                            </pc-button>
                        </div>
                    </template>
                </pc-table>
            </pc-loader>
        </template>
        <template v-slot:default>
            <div>
                <a
                    id="SendCommunicationLink"
                    v-bind:href="sendCommunicationUrl">
                    {{ sendCommunicationText }}
                </a>
            </div>
        </template>
    </pc-capsule>
</template>
<script>
    import capsuleBase from "./_sharedCapsuleBase";
    import { KEYS, NAMESPACE } from "./i18n/communicationsCapsule.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "../../I18n/common.i18n";
    import { mapI18n } from "Util/i18n";

    export default {
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "communications" },
            model: { type: Object, required: true },
            projectOwner: { type: Object, required: false, default: null } // non-admin only
        },
        data() {
            return {
                communications: [],
                processing: false,
                loadingTableData: false
            };
        },
        computed: {
            ...mapI18n({ ...KEYS }, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            capsuleBinderOverride() {
                return {
                    ...this.capsuleBinder,
                    title: this.calcTitle
                };
            },
            columns() {
                const descriptionColumn = {
                    header: this.descriptionHeader,
                    property: "Description",
                    isCardHeader: true
                };
                const subjectColumn = {
                    header: this.messageSubjectHeader,
                    property: "MessageSubject",
                    isCardHeader: true
                };
                const timestampColumn = {
                    header: this.sentTimestampHeader,
                    property: "SentTimestamp",
                    sortValue(comm) {
                        return comm.SentTimestamp.Ticks;
                    }
                };
                const statusColumn = {
                    header: this.messageStatusDisplayHeader,
                    property: "MessageStatusDisplay"
                };
                const recipientStatusColumn = {
                    header: this.messageStatusDisplayHeader,
                    property: "RecipientStatusDisplay"
                };

                const baseCols = this.isAdminPage
                    ? [timestampColumn, descriptionColumn, statusColumn]
                    : [timestampColumn, recipientStatusColumn, subjectColumn];

                return baseCols.map((c) => {
                    c.orderable = true;
                    c.searchable = true;
                    c.visible = true;
                    return c;
                });
            },
            calcTitle() {
                if (this.isAdminPage) {
                    return this.communicationsCapsuleTitle;
                } else if (!this.projectOwner) {
                    return this.communicationsTitleNonOwner;
                }
                return this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.communicationsTitleOwner,
                    values: { projectOwnerEmail: this.projectOwner.Email }
                });
            },
            sendCommunicationUrl() {
                if (!this.model || !this.model.EditCommunicationBaseUrl) {
                    return null;
                }
                return `${this.model.EditCommunicationBaseUrl}&FormId=${this.FormId}`;
            },
            emptyTable() {
                if (this.isAdminPage) {
                    return this.emptyTableText;
                }

                // Project owner should not actually be null if we're on the view/edit page, but
                // it might be null briefly when this is initially rendered
                return this.projectOwner != null
                    ? this.$app.res({
                          namespace: NAMESPACE,
                          key: KEYS.ownerNotNullText,
                          values: { projectOwnerEmail: this.projectOwner.Email }
                      })
                    : this.ownerNullText;
            },
            canSendCommunications() {
                return this.$app.LoggedInRole.RolePrivileges.ProjectAdminSendCommunication;
            },
            controller() {
                return this.$serviceControllers.Communications;
            }
        },
        watch: {
            projectOwner() {
                // When the project owner changes, refresh the received communications list
                if (!this.isAdminPage) {
                    this._loadTableData();
                }
            }
        },
        created() {
            this._loadTableData();
        },
        methods: {
            async _loadTableData() {
                let result;
                this.loadingTableData = true;
                if (this.isAdminPage) {
                    result = await this.controller.GetCommunicationHistory({
                        programPublicId: this.ProgramId,
                        projectPublicId: this.ProjectId
                    });
                    if (result.status === "success") {
                        this.communications = result.Data;
                        if (!this.canSendCommunications) {
                            this.communications = this.communications.filter((c) => !c.IsDraft);
                        }
                    }
                } else {
                    result = await this.controller.GetReceivedCommunications({
                        programPublicId: this.ProgramId,
                        projectPublicId: this.ProjectId
                    });
                    if (result.status === "success") {
                        this.communications = result.Data.communicationsList;
                    }
                }
                this.loadingTableData = false;
            },
            viewCommunication(communication) {
                const formIdParam = this.FormId ? `&FormId=${this.FormId}` : "";
                const url = `${this.model.ViewCommunicationBaseUrl}${formIdParam}&CommunicationId=${communication.PublicId}`;
                this.$app.openWindow(url, "_blank", "width=1000,height=1000");
            },
            editCommunication(communication) {
                const formIdParam = this.FormId ? `&FormId=${this.FormId}` : "";
                const url = `${this.model.EditCommunicationBaseUrl}${formIdParam}&CommunicationId=${communication.PublicId}`;
                this.$app.setHref(url);
            },
            async deleteDraftCommunication(communication) {
                if (this.processing) {
                    return;
                }

                const confirmDeletionDialogTitle = this.confirmDeletionDialogTitle;
                let confirmationMessage = this.deletionConfirmationMessage;
                if (communication.Description) {
                    confirmationMessage = this.$app.res({
                        namespace: NAMESPACE,
                        key: KEYS.deletionConfirmationWithDescMessage,
                        values: { communicationDescription: communication.Description }
                    });
                }

                if (!(await this.$app.showConfirmationDialog(confirmDeletionDialogTitle, confirmationMessage))) {
                    return;
                }

                try {
                    this.processing = true;
                    const result = await this.controller.DeleteDraftCommunication({
                        programId: this.ProgramId,
                        projectId: this.ProjectId,
                        communicationId: communication.PublicId
                    });
                    if (result.status === "success") {
                        this.communications = this.communications.filter((c) => {
                            return c.PublicId != communication.PublicId;
                        });
                    }
                } finally {
                    this.processing = false;
                }
            }
        }
    };
</script>
<style lang="scss" scoped>
    .communication-action-button {
        min-width: 5rem !important;
    }
</style>
